import { Injectable } from '@angular/core';
import * as _ from 'lodash';

import { UserService } from '../../services/core.user.service';
import { TourService } from '../tour/tour.service';
import { ProjectService } from '../../services/project.service';
import { ProjectModel } from '../../services/project.model.service';
import { StateService } from '@uirouter/angular';
import { MenuService } from '../../../widgets/mega-menu/mega-menu.service.component';
import { AccessDeniedService } from '../../services/core.access-denied.service';
import { RefusedError } from '../../services/core.refused-error';

@Injectable()
export class ProjectResolvesService {

    constructor(
        private userService: UserService,
        private tourService: TourService,
        private projectService: ProjectService,
        private projectModel: ProjectModel,
        private stateService: StateService,
        private menuService: MenuService,
        private accessDeniedService: AccessDeniedService
    ) {
    }

    user() {
        return this.userService.session.getUser();
    }

    projectTroveTour() {
        return this.tourService.setTour('projectCategoriesCtrl');
    }

    async project(transition: any, unixName: string): Promise<any> {
        try {
            const data = await this.projectService.details(unixName, { rel: 'permissions,plugins,parent,trackers,chat,roles'});
            const hasSessionUser = this.userService.session.hasUser();

            if (!data || !data.id || !data.unixName) {
                if (!hasSessionUser) {
                    return this.accessDeniedService.goLogin(transition);
                }
                return this.denied(transition, unixName);
            }

            const isPublicProject = data.isPublic === true || data.isPublic === 'Y';
            if (!isPublicProject && !hasSessionUser) {
                return this.accessDeniedService.goLogin(transition);
            }

            if (
                !isPublicProject &&
                hasSessionUser &&
                !this.userService.session.isSiteAdmin()
            ) {
                const permissions = data?.rel?.permissions;
                if (!this.accessDeniedService.hasAnyProjectPermission(permissions)) {
                    return this.denied(transition, unixName);
                }
            }

            this.projectModel.setCurrentProject(data);
            return data;
        } catch (response: any) {
            if (! this.userService.session.hasUser()) {
                return this.accessDeniedService.goLogin(transition);
            }

            if (response?.status === 401) {
                // The session may have died mid-navigation. `hasUser()` above is only the
                // local copy and can still say yes, so whether this is an expiry is not
                // something we can decide here — and the interceptor deliberately
                // whitelists this endpoint, so nothing else is going to look at it.
                // Refuse the standard way and let the error hook validate before acting.
                throw RefusedError.unauthorized('Project details refused; session may be gone');
            }

            if (response?.status === 403) {
                return this.denied(transition, unixName);
            }

            if (! this.stateService.current) {
                return this.accessDeniedService.goRefused(transition, {
                    excludeUnixName: unixName,
                    silent: true
                });
            }
            throw new Error(`Request failed with status: ${response.status}`);
        }
    }

    /**
     * The project we were navigating to is the one being refused, so it can never be the
     * landing spot — even when it is still the "current" project from the last transition.
     */
    private denied(transition: any, unixName: string): null {
        return this.accessDeniedService.goDenied(transition, { excludeUnixName: unixName });
    }

    isProjectAdmin(project: any) {
        return this.projectModel.hasPermission(project, 'admin');
    }

    async projectMenu(unixName: string) {
        try {
            const mega = await this.menuService.getMegaMenuForProject(unixName);

            if (mega) {
                this.menuService.setMenuData(mega);
                return _.get(mega, 'projectMenu', mega);
            } else {
                return {};
            }
        } catch (error) {
            return {};
        }
    }

    projectSummaryTour(transition: any) {
        // When a state has children, you have to avoid
        // inheriting the tour setting.
        if (transition.to().name == 'home.project.summary') {
            return this.tourService.setTour('projectHomeCtrl');
        }
    }

    async projectTeamSecurity(isProjectAdmin: any, transition: any) {
        if (this.userService.session.isSiteAdmin()) return Promise.resolve();

        if (! this.userService.session.hasUser()) {
            return Promise.reject(RefusedError.unauthorized('Project team admin requires a session'));
        }

        if (isProjectAdmin) {
            return Promise.resolve();
        }

        return Promise.reject(RefusedError.forbidden('User is not a project admin'));
    }

    sprintsTour() {
        return this.tourService.setTour('sprintHomeCtrl');
    }

    sprintDetail(id: string) {
        return this.projectService.sprintDetails(id);
    }

    sprintTour(sprint: any) {
        if (_.get(sprint, 'sprintStatus', 1) == 2) {
            return this.tourService.setTour('sprintinfoClosedHomeCtrl');
        }
        return this.tourService.setTour('sprintinfoHomeCtrl');
    }

    async standupSecurity(isProjectAdmin: any, transition: any) {
        if (this.userService.session.isSiteAdmin()) return Promise.resolve();

        if (! this.userService.session.hasUser()) {
            return Promise.reject(RefusedError.unauthorized('Standup requires a session'));
        }

        // Allow any authenticated team member (not just admins)
        // If user reached this point, they passed the project resolve which validates project access
        return Promise.resolve();
    }
}
